import { useCallback, useEffect, useState } from 'react';
import { listAllEmployees, peopleGateway } from '../../people';
import type { EmployeeResponse } from '../../people/types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { EmployeeDocumentsManager } from '../../employee-documents/components/EmployeeDocumentsManager';

// This page owns only the employee picker — the actual document behavior (upload form, validation, table, origin-aware actions) lives in the
// employee-scoped EmployeeDocumentsManager, shared with Employee Profile Setup's Step 3. This is the ONE document implementation; this page is a
// thin wrapper around it, not a second copy.
export function DocumentsPage() {
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // This picker needs the complete employee collection, not one page of it — follows every page of
      // the server-paginated Employee List query via the shared load-all helper.
      const all = await listAllEmployees(peopleGateway, { sortBy: 'name' });
      setEmployees(all);
      setEmployeeId((current) => current || all[0]?.id || '');
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load employees.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadEmployees();
  }, [loadEmployees]);

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Documents</h2>
      </div>

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">Loading employees…</p>}

      {!loading && (
        <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
          {employees.map((e) => (
            <option key={e.id} value={e.id}>{e.employeeNumber} — {e.firstName} {e.lastName}</option>
          ))}
        </select>
      )}

      {employeeId && <EmployeeDocumentsManager employeeId={employeeId} />}
    </section>
  );
}
